import React from 'react';
import { Alert, Box, Button, LinearProgress, Typography } from '@mui/material';
import { PhoneIphoneOutlined } from '@mui/icons-material';
import { GeoMeasureState, MEASURE_MS, MeasureStatus } from './useGeoMeasure';
import { measureQuality } from './geoMath';
import { alertSx, hintSx, MUTED, outlinedButtonSx, primaryButtonSx, softCardSx, stepNumberSx, TEXT } from './geoStyles';

/** Шаг 1 мастера: инструкция и живой замер браузера */
interface MeasureStepProps {
  measure: GeoMeasureState;
  /** Открыто на телефоне: другие подсказки про разрешения и GPS */
  compact: boolean;
  canPhone: boolean;
  onRetry: () => void;
  onMap: () => void;
  onPhone: () => void;
}

/** Почему замера нет и что делать; null - замер идет или удался */
function failureText(status: MeasureStatus, compact: boolean, canPhone: boolean): string | null {
  const orPhone = canPhone ? ' или настройте с телефона' : '';
  switch (status) {
    case 'denied':
      return compact
        ? 'Доступ к местоположению запрещен. Разрешите его для этого сайта в настройках браузера и нажмите «Повторить», или поставьте пин на карте вручную.'
        : `Доступ к местоположению запрещен для этого сайта. Нажмите на значок слева от адреса сайта, разрешите «Местоположение» и нажмите «Повторить». Или поставьте пин на карте вручную${orPhone}.`;
    case 'unavailable':
      return compact
        ? 'Телефон не смог определить место. Включите геолокацию (GPS) в настройках телефона и нажмите «Повторить», или поставьте пин на карте вручную.'
        : `Компьютер не смог определить место: обычно у него нет GPS. Поставьте пин на карте вручную${orPhone}.`;
    case 'insecure':
      return `Страница открыта без защищенного соединения (https), поэтому браузер не дает координаты. Поставьте пин на карте вручную${orPhone}.`;
    case 'unsupported':
      return `Браузер не умеет определять местоположение. Поставьте пин на карте вручную${orPhone}.`;
    default:
      return null;
  }
}

const MeasureStep: React.FC<MeasureStepProps> = ({ measure, compact, canPhone, onRetry, onMap, onPhone }) => {
  const steps = [
    compact ? 'Разрешите доступ к местоположению, когда браузер спросит' : 'Разрешите доступ к местоположению: браузер спросит в окне у адресной строки',
    'Подождите до 25 секунд: с каждым замером точность растет',
    'На карте подвиньте карту так, чтобы пин стоял точно на входе в аптеку'
  ];
  const failure = failureText(measure.status, compact, canPhone);
  const best = measure.best;
  const quality = best ? measureQuality(best.accuracy) : null;
  const canRetry = measure.status === 'done' || measure.status === 'denied' || measure.status === 'unavailable';
  const secondsLeft = Math.max(0, Math.ceil((MEASURE_MS - measure.elapsedMs) / 1000));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {steps.map((text, index) => (
          <Box key={text} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
            <Box sx={stepNumberSx}>{index + 1}</Box>
            <Typography sx={{ color: TEXT, fontSize: '0.9rem', lineHeight: 1.5 }}>{text}</Typography>
          </Box>
        ))}
      </Box>

      {failure ? (
        <Alert severity={measure.status === 'denied' ? 'warning' : 'info'} sx={alertSx}>{failure}</Alert>
      ) : (
        <Box sx={softCardSx}>
          {measure.status === 'measuring' && (
            <LinearProgress variant="determinate" value={Math.min(100, (measure.elapsedMs / MEASURE_MS) * 100)} sx={{ mb: 1.5, height: 6, borderRadius: 3 }} />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 700, color: TEXT }}>
              {best ? `${measure.status === 'measuring' ? 'Сейчас' : 'Лучший замер'} ±${Math.round(best.accuracy)} м` : 'Ждем ответ устройства'}
            </Typography>
            {measure.status === 'measuring' && <Typography sx={{ color: MUTED, fontSize: 13 }}>еще {secondsLeft} с</Typography>}
          </Box>
          {quality && <Typography sx={{ color: quality.color, fontWeight: 600, fontSize: 13.5, mt: 0.25 }}>{quality.label}</Typography>}
          {!compact && (
            <Typography sx={{ ...hintSx, mt: 1 }}>
              На компьютере обычно нет GPS, поэтому замер грубый: точку вы поправите на карте. Точнее всего - с телефона, стоя у аптеки.
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
        {canPhone && (
          <Button variant="outlined" startIcon={<PhoneIphoneOutlined />} onClick={onPhone} sx={{ ...outlinedButtonSx, mr: 'auto' }}>
            С телефона
          </Button>
        )}
        {canRetry && <Button variant="outlined" onClick={onRetry} sx={outlinedButtonSx}>Повторить</Button>}
        <Button variant="contained" onClick={onMap} sx={primaryButtonSx}>
          {failure ? 'Поставить на карте' : 'Перейти к карте'}
        </Button>
      </Box>
    </Box>
  );
};

export default MeasureStep;
